// InviteUser.jsx - FIXED: All issues resolved
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  ContentCopy as CopyIcon,
  Email as EmailIcon,
  AccessTime as TimeIcon,
  Share as ShareIcon,
  Send as SendIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import { useMutation } from '../../hooks/useApi';
import { api } from '../../services/api';
import { useNotification } from '../common/GlobalNotifications';
import { formatRelativeTime } from '../../utils/formatters';

const InviteUser = ({ open, onClose, onInviteCreated }) => {
  const { showSuccess, showError } = useNotification();
  const { t } = useTranslation();

  const [invitationData, setInvitationData] = useState(null);
  const [step, setStep] = useState(t('hardcoded.components_auth_createusermanually.create'));

  const validationSchema = Yup.object({
    invite_email: Yup.string()
      .email(t('inviteUser.validation.emailInvalid'))
      .required(t('inviteUser.validation.emailRequired')),
    user_type: Yup.string()
      .oneOf(['user', 'manager', 'admin'], t('inviteUser.validation.roleInvalid'))
      .required(t('inviteUser.validation.roleRequired')),
    expires_in_days: Yup.number()
      .min(1, t('inviteUser.validation.expirationMin'))
      .max(30, t('inviteUser.validation.expirationMax'))
      .required(t('inviteUser.validation.expirationRequired')),
  });

  const roleOptions = [
    {
      value: 'user',
      label: t('inviteUser.roles.user.label'),
      description: t('inviteUser.roles.user.description'),
      color: 'default'
    },
    {
      value: 'manager',
      label: t('inviteUser.roles.manager.label'),
      description: t('inviteUser.roles.manager.description'),
      color: 'warning'
    },
    {
      value: 'admin',
      label: t('inviteUser.roles.admin.label'),
      description: t('inviteUser.roles.admin.description'),
      color: 'error'
    }
  ];

  const { mutate: createInvitation, loading } = useMutation(
    api.auth.createInvitation,
    {
      onSuccess: (response) => {
        console.log(t('hardcoded.components_auth_inviteuser.invitation_created_successfully'), response);
        const data = response.data || response;

        setInvitationData(data);
        setStep('success');
        showSuccess(
          data.email_sent
            ? t('inviteUser.success.emailSent')
            : t('inviteUser.success.emailNotSent')
        );

        if (onInviteCreated) {
          onInviteCreated(data);
        }
      },
      onError: (error) => {
        console.error(t('hardcoded.components_auth_inviteuser.error_creating_invitation'), error);
        const errorMessage = error.response?.data?.detail ||
          error.message ||
          t('inviteUser.notifications.invitationError');
        showError(errorMessage);
      }
    }
  );

  const formik = useFormik({
    initialValues: {
      invite_email: '',
      user_type: 'user',
      expires_in_days: 7,
    },
    validationSchema,
    onSubmit: (values) => {
      console.log('Creating invitation with values:', values);
      createInvitation(values);
    },
  });

  const handleClose = () => {
    setStep(t('hardcoded.components_auth_createusermanually.create'));
    setInvitationData(null);
    formik.resetForm();
    onClose();
  };

  const copyToClipboard = async (text, successMessage) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccess(successMessage || t('inviteUser.notifications.codeCopied'));
    } catch (error) {
      showError(t('inviteUser.notifications.copyError'));
    }
  };

  const shareInvitation = async () => {
    const inviteUrl = `${window.location.origin}/register?invite=${invitationData.invitation_code}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: t('hardcoded.components_auth_inviteuser.invitacion_a_arsenal_preventive_sensors'),
          text: `Has sido invitado a unirte a ${invitationData.company_name}`,
          url: inviteUrl,
        });
      } catch (error) {
        // User cancelled sharing
      }
    } else {
      copyToClipboard(inviteUrl, t('inviteUser.notifications.linkCopied'));
    }
  };

  const sendEmail = () => {
    const inviteUrl = `${window.location.origin}/register?invite=${invitationData.invitation_code}`;
    const roleDisplay = roleOptions.find(r => r.value === invitationData.user_type)?.label || t('inviteUser.roles.user.label');

    const subject = encodeURIComponent(t('hardcoded.components_auth_inviteuser.invitacion_a_arsenal_preventive_sensors', { company_name: invitationData.company_name }));
    const body = encodeURIComponent(
      t('hardcoded.components_auth_inviteuser.has_sido_invitado_a_unirte', { company_name: invitationData.company_name }) +
      t('hardcoded.components_auth_inviteuser.rol_asignado_roledisplay', { roleDisplay: roleDisplay }) +
      t('hardcoded.components_auth_inviteuser.codigo_de_invitacion_invitation_code', { invitation_code: invitationData.invitation_code }) +
      t('hardcoded.components_auth_inviteuser.o_haz_clic_en_este', { inviteUrl: inviteUrl }) +
      t('hardcoded.components_auth_inviteuser.esta_invitacion_expira_el_newdateinvitati', { newDateinvitati: new Date(invitationData.expires_at).toLocaleDateString() })
    );

    window.open(`mailto:${invitationData.invite_email}?subject=${subject}&body=${body}`);
  };

  const selectedRole = roleOptions.find(role => role.value === formik.values.user_type);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PersonAddIcon color="primary" />
          <Typography variant="h6" component="div">
            {step === 'create' ? t('inviteUser.title') : t('inviteUser.successTitle')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {step === 'create' ? (
          <Box>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {t('inviteUser.subtitle')}
            </Typography>

            <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 3 }}>
              <TextField
                fullWidth
                name="invite_email"
                label={t('inviteUser.form.email')}
                type="email"
                value={formik.values.invite_email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.invite_email && Boolean(formik.errors.invite_email)}
                helperText={
                  formik.touched.invite_email && formik.errors.invite_email
                    ? formik.errors.invite_email
                    : t('inviteUser.form.emailHelper')
                }
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />
                }}
              />

              <FormControl
                fullWidth
                sx={{ mb: 3 }}
                error={formik.touched.user_type && Boolean(formik.errors.user_type)}
              >
                <InputLabel>{t('inviteUser.form.role')}</InputLabel>
                <Select
                  name="user_type"
                  value={formik.values.user_type}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label={t('inviteUser.form.role')}
                >
                  {roleOptions.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Chip
                          label={role.label}
                          color={role.color}
                          size="small"
                          sx={{ minWidth: 80 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {role.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {formik.touched.user_type && formik.errors.user_type
                    ? formik.errors.user_type
                    : selectedRole?.description || t('inviteUser.form.roleHelper')
                  }
                </FormHelperText>
              </FormControl>

              <TextField
                fullWidth
                name="expires_in_days"
                label={t('inviteUser.form.expirationDays')}
                type="number"
                value={formik.values.expires_in_days}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.expires_in_days && Boolean(formik.errors.expires_in_days)}
                helperText={
                  formik.touched.expires_in_days && formik.errors.expires_in_days
                    ? formik.errors.expires_in_days
                    : t('inviteUser.form.expirationHelper')
                }
                inputProps={{
                  min: 1,
                  max: 30
                }}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: <TimeIcon sx={{ mr: 1, color: 'action.active' }} />
                }}
              />

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{t('inviteUser.whatHappens.title')}</strong>
                  <br />• {t('inviteUser.whatHappens.emailSent')}
                  <br />• {t('inviteUser.whatHappens.roleAssigned')} <strong>{selectedRole?.label}</strong>
                  <br />• {t('inviteUser.whatHappens.autoJoin')}
                  <br />• {t('inviteUser.whatHappens.changePermissions')}
                </Typography>
              </Alert>
            </Box>
          </Box>
        ) : (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon />
                <Typography variant="body2">
                  <strong>{t('inviteUser.notifications.invitationCreated')}!</strong>
                  {invitationData?.email_sent
                    ? ` ${t('inviteUser.success.emailSentNote')}`
                    : ` (${t('inviteUser.success.emailNotSentNote')})`
                  }
                </Typography>
              </Box>
            </Alert>

            <Paper
              variant="outlined"
              sx={{
                p: 3,
                mb: 3,
                backgroundColor: 'grey.50',
                border: '2px dashed',
                borderColor: 'primary.main',
              }}
            >
              <Typography variant="subtitle2" gutterBottom color="primary">
                {t('inviteUser.success.details')}
              </Typography>

              <Box sx={{ display: 'grid', gap: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Email:</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {invitationData?.invite_email}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('users.table.role')}:
                  </Typography>
                  <Chip
                    label={roleOptions.find(r => r.value === invitationData?.user_type)?.label || t('inviteUser.roles.user.label')}
                    color={roleOptions.find(r => r.value === invitationData?.user_type)?.color || 'default'}
                    size="small"
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('audit.detail.expires')}:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {invitationData?.expires_at ? formatRelativeTime(invitationData.expires_at) : 'N/A'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('audit.detail.emailSent')}:
                  </Typography>
                  <Chip
                    label={invitationData?.email_sent ? t('common.yes') : t('common.no')}
                    color={invitationData?.email_sent ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom color="primary">
                {t('inviteUser.success.invitationCode')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography
                  variant="h6"
                  component="code"
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    letterSpacing: 1,
                    color: 'primary.main',
                    flex: 1,
                    padding: 1,
                    backgroundColor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                >
                  {invitationData?.invitation_code}
                </Typography>
                <Tooltip title={t('inviteUser.success.copyCode')}>
                  <IconButton
                    onClick={() => copyToClipboard(
                      invitationData?.invitation_code,
                      t('inviteUser.notifications.codeCopied')
                    )}
                    size="small"
                    color="primary"
                  >
                    <CopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>

            <Divider sx={{ my: 2 }}>
              <Chip label={t('inviteUser.success.shareOptions')} size="small" />
            </Divider>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
              {!invitationData?.email_sent && (
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={sendEmail}
                  size="small"
                  color="primary"
                >
                  {t('inviteUser.success.sendByEmail')}
                </Button>
              )}

              <Button
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={shareInvitation}
                size="small"
              >
                {t('inviteUser.success.shareLink')}
              </Button>

              <Button
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={() => copyToClipboard(
                  `${window.location.origin}/register?invite=${invitationData?.invitation_code}`,
                  t('inviteUser.notifications.linkCopied')
                )}
                size="small"
              >
                {t('inviteUser.success.copyLink')}
              </Button>
            </Box>

            <Alert severity={invitationData?.email_sent ? 'success' : 'warning'} sx={{ mt: 3 }}>
              <Typography variant="body2">
                {invitationData?.email_sent ? (
                  <>
                    <strong>✅ {t('inviteUser.success.emailSentNote')}</strong>
                  </>
                ) : (
                  <>
                    <strong>⚠️ {t('inviteUser.success.emailNotSentNote')}</strong>
                  </>
                )}
                <br />
                <strong>{t('common.warning')}:</strong> {t('inviteUser.success.importantNote')}
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          color="inherit"
        >
          {step === 'create' ? t('common.cancel') : t('common.close')}
        </Button>
        {step === 'create' && (
          <Button
            onClick={formik.handleSubmit}
            variant="contained"
            disabled={loading || !formik.isValid}
            sx={{ minWidth: 140 }}
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {loading ? t('inviteUser.form.creating') : t('inviteUser.form.createInvitation')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InviteUser;